import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Sparkles,
  Upload,
  X,
  ChevronRight,
  ChevronLeft,
  Music,
  MapPin,
  Plane,
  Car,
  Building,
  User,
  Zap,
  Search,
  Loader2,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import PlacesAutocomplete, {
  geocodeByAddress,
} from "react-places-autocomplete";
import { API_ENDPOINTS } from "../lib/config";
import { AuthService, fetchWithAuth } from "../lib/authService";
import { openPaytmCheckout } from "../lib/paytmService";

const STEP_TITLES = [
  "Your Category",
  "Your Subscription",
  "Tell Us About You",
  "Where You Perform",
];

const TRAVEL_OPTIONS = [
  { id: "within_city", label: "Within City", icon: Building },
  { id: "outside_city", label: "Outside City", icon: Car },
  { id: "across_states", label: "Across States", icon: Plane },
  { id: "international", label: "International", icon: Zap },
];

export default function SignupForm() {
  const navigate = useNavigate();

  // Navigation and step state (0=Category, 1=Subscription, 2=Profile, 3=Location)
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 0: Roles & Specialties
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showSpecialtiesModal, setShowSpecialtiesModal] = useState(false);
  const [specialtiesSearch, setSpecialtiesSearch] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);

  // Step 1: Subscriptions
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [featuresList, setFeaturesList] = useState([]);
  const [banners, setBanners] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [billingInterval, setBillingInterval] = useState("annual"); // "quarterly" or "annual"
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [payingSubscription, setPayingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Step 2: Profile info
  const [stageName, setStageName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("Male");
  const [dob, setDob] = useState({ day: "", month: "", year: "" });
  const [bio, setBio] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Step 3: Location
  const [city, setCity] = useState("");
  const [area, setArea] = useState("");
  const [state, setState] = useState("");
  const [selectedTravel, setSelectedTravel] = useState(["within_city"]);
  const [additionalCities, setAdditionalCities] = useState([]);
  const [newCityInput, setNewCityInput] = useState("");


  // Default genres
  const defaultGenres = useMemo(
    () => [
      "House",
      "Techno",
      "Hip Hop",
      "EDM",
      "Commercial",
      "Bollywood",
      "Deep House",
      "Retro",
      "Punjabi",
      "Afrobeat",
      "Rock",
      "Pop",
      "Sufi",
      "Acoustic",
    ],
    []
  );

  // Fetch Performer Roles
  useEffect(() => {
    let isMounted = true;
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const res = await fetch(API_ENDPOINTS.PERFORMER_ROLES);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            if (isMounted) {
              setRoles(data);
              setSelectedRole(data[0]);
            }
            return;
          }
        }
        throw new Error("Unable to fetch roles from API");
      } catch (err) {
        console.error("Error fetching performer roles, applying fallback:", err);
        if (isMounted) {
          const fallbackRoles = [
            { id: "69c106fd1cc204a317dc9d4b", name: "dj", displayName: "DJ", emoji: "🎧", description: "EDM, House, Commercial & Bollywood live sets", genres: defaultGenres },
            { id: "69c1126f1cc204a317dc9df0", name: "live_singer", displayName: "Live Singer", emoji: "🎤", description: "Solo acoustics, vocals & live stage performance", genres: defaultGenres },
            { id: "69c1128f1cc204a317dc9df5", name: "live_band", displayName: "Live Band", emoji: "🥁", description: "Multi-instrumentalist live band & orchestra", genres: defaultGenres },
            { id: "69c112af1cc204a317dc9dfa", name: "anchor", displayName: "Anchor", emoji: "🎙️", description: "Event hosts, stage emcees & crowd animators", genres: defaultGenres },
          ];
          setRoles(fallbackRoles);
          setSelectedRole(fallbackRoles[0]);
        }
      } finally {
        if (isMounted) {
          setLoadingRoles(false);
        }
      }
    };
    fetchRoles();
    return () => {
      isMounted = false;
    };
  }, [defaultGenres]);

  // Fetch Subscription Plans from Paytm API - strictly from API response only
  const fetchPlans = useCallback(async () => {
    setLoadingPlans(true);
    setSubscriptionError(null);
    try {
      const paytmRes = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION_PLANS);
      if (paytmRes.ok) {
        const json = await paytmRes.json();
        if (Array.isArray(json.plans) && json.plans.length > 0) {
          setSubscriptionPlans(json.plans);
          setSelectedPlanIndex(0);
        } else {
          setSubscriptionPlans([]);
          setSubscriptionError("No subscription plans found.");
        }
        if (Array.isArray(json.banners)) {
          setBanners(json.banners);
        } else {
          setBanners([]);
        }
        if (Array.isArray(json.features)) {
          setFeaturesList(json.features);
        } else {
          setFeaturesList([]);
        }
      } else {
        const errJson = await paytmRes.json().catch(() => ({}));
        const errMsg = errJson.message || "Failed to load subscription plans.";
        setSubscriptionError(errMsg);
        setSubscriptionPlans([]);
      }
    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setSubscriptionError("Failed to fetch subscription plans. Please retry.");
      setSubscriptionPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Banner carousel auto-rotation
  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => {
      setActiveBannerIndex((prev) => (prev + 1) % banners.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [banners]);

  // Filtered Specialties list
  const currentRoleGenres = useMemo(() => {
    const g =
      selectedRole?.genres && selectedRole.genres.length > 0
        ? selectedRole.genres
        : defaultGenres;
    if (!specialtiesSearch.trim()) return g;
    return g.filter((item) =>
      item.toLowerCase().includes(specialtiesSearch.toLowerCase())
    );
  }, [selectedRole, defaultGenres, specialtiesSearch]);

  const handleRoleCardClick = (role) => {
    setSelectedRole(role);
    setSpecialtiesSearch("");
    setShowSpecialtiesModal(true);
  };

  const toggleSpecialty = (genre) => {
    setSelectedSpecialties((prev) => {
      if (prev.includes(genre)) {
        return prev.filter((g) => g !== genre);
      }
      if (prev.length >= 3) {
        toast.warning("You can select a maximum of 3 specialities");
        return prev;
      }
      return [...prev, genre];
    });
  };

  const handleSpecialtiesContinue = () => {
    if (selectedSpecialties.length === 0) {
      toast.error("Please select at least one specialty");
      return;
    }
    if (selectedSpecialties.length > 3) {
      toast.warning("You can select a maximum of 3 specialities");
      return;
    }
    setShowSpecialtiesModal(false);
    setCurrentStep(1); // Advance to Subscription
  };

  // Avatar Image Upload
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      toast.error("Please upload a JPG or PNG image");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setProfileImage(URL.createObjectURL(file));
    setUploadingAvatar(true);

    const token = localStorage.getItem("tringAuthToken");
    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const res = await fetch(API_ENDPOINTS.PARTNER_UPLOAD_AVATAR, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to upload avatar");
      }

      const json = await res.json();
      setAvatarUrl(json.avatarUrl || json.url || "");
      toast.success("Profile photo uploaded!");
    } catch (err) {
      console.error("Avatar upload failed:", err);
      toast.error(err.message || "Could not upload photo");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Extract city, locality, and state from Google Places details or geocoding
  const extractLocationDetails = useCallback(async (address, placeId) => {
    try {
      let components = null;
      let formattedAddress = address;

      if (placeId && window.google?.maps?.places) {
        try {
          const dummyDiv = document.createElement("div");
          const service = new window.google.maps.places.PlacesService(dummyDiv);
          const details = await new Promise((resolve, reject) => {
            service.getDetails(
              { placeId, fields: ["address_components", "formatted_address", "name"] },
              (placeResult, status) => {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  placeResult
                ) {
                  resolve(placeResult);
                } else {
                  reject(status);
                }
              }
            );
          });
          if (details?.address_components) {
            components = details.address_components;
            formattedAddress = details.formatted_address || address;
          }
        } catch (e) {
          console.warn("PlacesService getDetails fallback to geocodeByAddress:", e);
        }
      }

      if (!components) {
        const results = await geocodeByAddress(address);
        if (results && results[0]) {
          components = results[0].address_components;
          formattedAddress = results[0].formatted_address || address;
        }
      }

      if (!components) {
        return {
          city: address.split(",")[0]?.trim() || address,
          area: "",
          state: "",
          formattedAddress,
        };
      }

      let sublocality = "";
      let locality = "";
      let adminArea2 = "";
      let adminArea1 = "";

      for (const comp of components) {
        const types = comp.types || [];
        if (types.includes("sublocality_level_1") || types.includes("sublocality")) {
          sublocality = comp.long_name || comp.short_name || "";
        } else if (types.includes("neighborhood") && !sublocality) {
          sublocality = comp.long_name || comp.short_name || "";
        }

        if (types.includes("locality")) {
          locality = comp.long_name || comp.short_name || "";
        } else if (types.includes("postal_town") && !locality) {
          locality = comp.long_name || comp.short_name || "";
        } else if (types.includes("administrative_area_level_2")) {
          adminArea2 = comp.long_name || comp.short_name || "";
        }

        if (types.includes("administrative_area_level_1")) {
          adminArea1 = comp.long_name || comp.short_name || "";
        }
      }

      const detectedCity = locality || adminArea2 || address.split(",")[0]?.trim() || "";
      const detectedArea = sublocality || (detectedCity !== locality && locality ? locality : "");
      const detectedState = adminArea1;

      return {
        city: detectedCity,
        area: detectedArea,
        state: detectedState,
        formattedAddress,
      };
    } catch (err) {
      console.error("Error extracting location details:", err);
      return {
        city: address.split(",")[0]?.trim() || address,
        area: "",
        state: "",
        formattedAddress: address,
      };
    }
  }, []);

  const handleSelectPrimaryCity = async (address, placeId) => {
    setCity(address.split(",")[0]?.trim() || address);
    try {
      const loc = await extractLocationDetails(address, placeId);
      if (loc.city) setCity(loc.city);
      if (loc.state) setState(loc.state);
      if (loc.area && !area) setArea(loc.area);
    } catch (err) {
      console.error("Error selecting primary city:", err);
    }
  };

  const handleSelectLocality = async (address, placeId) => {
    setArea(address.split(",")[0]?.trim() || address);
    try {
      const loc = await extractLocationDetails(address, placeId);
      if (loc.area) setArea(loc.area);
      if (loc.city && !city) setCity(loc.city);
      if (loc.state && !state) setState(loc.state);
    } catch (err) {
      console.error("Error selecting locality:", err);
    }
  };

  const handleSelectOtherCity = async (address, placeId) => {
    try {
      const loc = await extractLocationDetails(address, placeId);
      const resolved = (loc.city || address.split(",")[0] || "").trim();
      if (resolved && !additionalCities.includes(resolved)) {
        setAdditionalCities((prev) => [...prev, resolved]);
      }
    } catch (err) {
      console.error("Error selecting other city:", err);
      const fallback = address.split(",")[0]?.trim();
      if (fallback && !additionalCities.includes(fallback)) {
        setAdditionalCities((prev) => [...prev, fallback]);
      }
    } finally {
      setNewCityInput("");
    }
  };

  // Travel Options toggle
  const toggleTravelOption = (id) => {
    setSelectedTravel((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Additional City add/remove
  const handleAddCity = () => {
    const trimmed = newCityInput.trim();
    if (trimmed) {
      const cityName = trimmed.split(",")[0]?.trim() || trimmed;
      if (cityName && !additionalCities.includes(cityName)) {
        setAdditionalCities([...additionalCities, cityName]);
      }
      setNewCityInput("");
    }
  };

  const handleRemoveCity = (cityToRemove) => {
    setAdditionalCities(additionalCities.filter((c) => c !== cityToRemove));
  };

  // Validations
  const validateStep2 = () => {
    if (!stageName.trim()) {
      toast.error("Please enter your stage name");
      return false;
    }
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!dob.day || !dob.month || !dob.year) {
      toast.error("Please enter your date of birth");
      return false;
    }

    const birthDate = new Date(parseInt(dob.year, 10), parseInt(dob.month, 10) - 1, parseInt(dob.day, 10));
    const ageDiffMs = Date.now() - birthDate.getTime();
    const ageDate = new Date(ageDiffMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    if (age < 16) {
      toast.error("You must be at least 16 years old to register");
      return false;
    }

    return true;
  };

  const validateStep3 = () => {
    if (!city.trim()) {
      toast.error("Please enter your primary performance city");
      return false;
    }
    return true;
  };

  // Paytm Payment & Subscription Purchase
  const handleSubscriptionPayment = async () => {
    setSubscriptionError(null);
    if (!subscriptionPlans || subscriptionPlans.length === 0) {
      const err = "No subscription plans loaded. Please retry.";
      setSubscriptionError(err);
      toast.error(err);
      return;
    }
    const plan = subscriptionPlans[selectedPlanIndex] || subscriptionPlans[0];
    const interval = billingInterval === "annual" ? plan.annual : plan.quarterly;
    const planId = interval?.id;

    if (!planId) {
      const err = "Selected plan ID is missing from API. Please select a valid plan.";
      setSubscriptionError(err);
      toast.error(err);
      return;
    }

    setPayingSubscription(true);

    try {
      const userPhone = localStorage.getItem("number") || "";
      console.log("Initiating subscription with planId:", planId, "userPhone:", userPhone);
      const res = await fetchWithAuth(API_ENDPOINTS.PAYTM_SUBSCRIPTION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: planId.trim(),
          ...(userPhone ? { mobile: userPhone, phoneNumber: userPhone, phone: userPhone } : {}),
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const errMsg =
          errData.message ||
          errData.error ||
          `Backend returned ${res.status}: Failed to create subscription.`;
        console.error("Paytm subscription create failed:", res.status, errData);
        setSubscriptionError(errMsg);
        toast.error(errMsg);
        setPayingSubscription(false);
        // CRITICAL: Stop execution, do NOT proceed!
        return;
      }

      const data = await res.json();
      console.log("Paytm subscription create response:", data);

      if (!data.id || !data.txnToken || !data.mid) {
        const errMsg = "Payment tokens missing from server response. Please retry.";
        setSubscriptionError(errMsg);
        toast.error(errMsg);
        setPayingSubscription(false);
        // CRITICAL: Stop execution!
        return;
      }

      // Launch Paytm CheckoutJS SDK
      await openPaytmCheckout({
        orderId: data.id,
        txnToken: data.txnToken,
        amount: data.currentAmount || data.mandateMaxAmount || interval?.amount,
        mid: data.mid,
        callbackUrl: data.callbackUrl,
        mobileNumber: userPhone,
        onSuccess: async () => {
          console.log("Paytm payment success, verifying orderId:", data.id);
          try {
            const verifyRes = await fetchWithAuth(
              API_ENDPOINTS.PAYTM_SUBSCRIPTION_VERIFY,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ orderId: data.id }),
              }
            );

            if (verifyRes.ok) {
              toast.success("Subscription Activated!");
              setPayingSubscription(false);
              localStorage.setItem("isUserRegistered", "true");
              AuthService.setUserRegistered(true);
              setCurrentStep(2); // Advance ONLY on genuine verified success!
            } else {
              const verifyErrData = await verifyRes.json().catch(() => ({}));
              const verifyErrMsg =
                verifyErrData.message ||
                "Payment completed, but verification failed on server. Please check status.";
              setSubscriptionError(verifyErrMsg);
              toast.error(verifyErrMsg);
              setPayingSubscription(false);
            }
          } catch (vErr) {
            console.error("Verification error:", vErr);
            setSubscriptionError("Verification request failed. Please check your network and retry.");
            toast.error("Verification request failed. Please retry.");
            setPayingSubscription(false);
          }
        },
        onError: (err) => {
          console.error("Paytm transaction error:", err);
          const errMsg = err?.message || "Payment cancelled or failed. Please try again.";
          setSubscriptionError(errMsg);
          toast.error(errMsg);
          setPayingSubscription(false);
        },
        onClose: () => {
          setPayingSubscription(false);
        },
      });
    } catch (err) {
      console.error("Subscription payment initiation error:", err);
      const errMsg = err.message || "Network error initiating subscription. Please retry.";
      setSubscriptionError(errMsg);
      toast.error(errMsg);
      setPayingSubscription(false);
    }
  };

  // Final Partner Registration
  const handleCompleteRegistration = async () => {
    if (!validateStep3()) return;

    setIsSubmitting(true);
    const token = localStorage.getItem("tringAuthToken");
    if (!token) {
      toast.error("Authentication expired. Please log in again.");
      navigate("/");
      return;
    }

    const roleName = selectedRole?.name || "DJ";
    const cleanedStage = stageName.replace(new RegExp(`^${roleName}\\s+`, "i"), "").trim();
    const fullDisplayName = `${roleName} ${cleanedStage}`.trim();

    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    const dobString = `${dob.year}-${dob.month.toString().padStart(2, "0")}-${dob.day.toString().padStart(2, "0")}`;

    const rawRoleId = selectedRole?._id || selectedRole?.id || null;
    const isObjectId = typeof rawRoleId === "string" && /^[0-9a-fA-F]{24}$/.test(rawRoleId);
    const validRoleId = isObjectId ? rawRoleId : null;

    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: email.trim(),
      phone_number: localStorage.getItem("number") || "",
      gender: gender,
      display_name: fullDisplayName,
      dob: dobString,
      referredBy: localStorage.getItem("referralCode") || null,
      location: city.trim(),
      locality: area.trim(),
      state: state.trim(),
      bio: bio.trim(),
      roleId: validRoleId,
      category: roleName,
      specialties: selectedSpecialties,
      travelRange: selectedTravel,
      otherCities: additionalCities,
      avatar: avatarUrl || null,
      isLive: false,
      isBusinessUser: false,
    };

    try {
      const res = await fetchWithAuth(API_ENDPOINTS.PARTNER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Registration failed");
      }

      localStorage.setItem("isBusiness", "false");
      localStorage.setItem("isUserRegistered", "true");
      AuthService.setUserRegistered(true);
      toast.success("Registration complete!");
      navigate("/loggedinartist", { replace: true });
    } catch (err) {
      console.error("Registration error:", err);
      toast.error(err.message || "An error occurred during registration.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      AuthService.clearTokens();
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {}
      navigate("/", { replace: true });
    }
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 70 }, (_, i) => currentYear - 16 - i);

  // Active plans list - strictly from API response only
  const currentPlans = subscriptionPlans;
  const currentSelectedPlan = currentPlans[selectedPlanIndex] || currentPlans[0];
  const selectedTierName =
    currentSelectedPlan?.type?.toLowerCase()?.replace(/^\w/, (c) => c.toUpperCase()) || "Plan";

  const bannerList = banners;

  return (
    <div className="relative min-h-screen w-full bg-[#F6F7FA] text-gray-900 font-sans flex flex-col justify-between">
      <ToastContainer position="top-center" autoClose={3500} />

      {/* Top Header Bar with responsive Stepper */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-700 transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">
                {STEP_TITLES[currentStep] || "Partner Onboarding"}
              </h1>
              <p className="text-[11px] text-gray-400 hidden sm:block">
                Step {currentStep + 1} of 4 • Tringbox Partner Setup
              </p>
            </div>
          </div>

          {/* Desktop/Tablet Stepper Indicator */}
          <div className="hidden md:flex items-center gap-2">
            {STEP_TITLES.map((title, idx) => {
              const isPast = idx < currentStep;
              const isCurrent = idx === currentStep;
              return (
                <div key={title} className="flex items-center gap-2">
                  <div
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${
                      isCurrent
                        ? "bg-[#6720A4] text-white shadow-xs"
                        : isPast
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <span>{isPast ? "✓" : idx + 1}</span>
                    <span>{title}</span>
                  </div>
                  {idx < STEP_TITLES.length - 1 && (
                    <div className="w-3 h-0.5 bg-gray-200" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Mobile Badge */}
          <div className="md:hidden text-xs font-bold px-2.5 py-1 rounded-full bg-purple-100 text-[#6720A4]">
            {`${currentStep + 1} / 4`}
          </div>
        </div>
      </header>

      {/* Main Content Area - Responsive Container for Mobile, Tablet, and Desktop */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <AnimatePresence mode="wait">
          {/* STEP 0: "Your Category" */}
          {currentStep === 0 && (
            <motion.div
              key="step-category"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-5xl mx-auto space-y-6"
            >
              <div className="text-center space-y-2">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
                  What do you do?
                </h2>
                <p className="text-sm sm:text-base text-gray-500 font-medium max-w-xl mx-auto">
                  Organisers search by category to find performers. Pick your primary category to continue.
                </p>
              </div>

              {loadingRoles ? (
                <div className="py-24 flex flex-col items-center justify-center space-y-3">
                  <Loader2 className="w-9 h-9 animate-spin text-[#6720A4]" />
                  <p className="text-sm text-gray-500 font-medium">Loading categories...</p>
                </div>
              ) : (
                <div
                  className={`grid gap-6 mx-auto items-stretch ${
                    roles.length === 3
                      ? "grid-cols-1 md:grid-cols-3 max-w-4xl"
                      : roles.length === 2
                      ? "grid-cols-1 sm:grid-cols-2 max-w-2xl"
                      : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl"
                  }`}
                >
                  {roles.map((role) => {
                    const isSelected = Boolean(
                      (selectedRole?.id && role.id && selectedRole.id === role.id) ||
                      (selectedRole?._id && role._id && selectedRole._id === role._id) ||
                      (selectedRole?.name && role.name && selectedRole.name.toLowerCase() === role.name.toLowerCase())
                    );
                    const roleEmoji =
                      role.emoji ||
                      (role.name.toLowerCase().includes("dj")
                        ? "🎧"
                        : role.name.toLowerCase().includes("singer")
                        ? "🎤"
                        : role.name.toLowerCase().includes("band")
                        ? "🥁"
                        : "🎙️");

                    return (
                      <div
                        key={role.id || role._id || role.name}
                        onClick={() => handleRoleCardClick(role)}
                        className={`p-6 rounded-3xl border-2 transition-all cursor-pointer bg-white flex flex-col justify-between h-full hover:shadow-lg ${
                          isSelected
                            ? "border-[#6720A4] shadow-lg ring-2 ring-[#6720A4]/20"
                            : "border-gray-200/80 hover:border-purple-300"
                        }`}
                      >
                        <div>
                          <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center text-3xl mb-4">
                            <span>{roleEmoji}</span>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {role.displayName || role.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">
                            {role.description || "Live stage & venue performing artist"}
                          </p>
                        </div>

                        <div className="mt-6 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-[#6720A4]">
                          <span>Select Specialties</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}

          {/* STEP 1: "Your Subscription" - Full Responsive 2-Column Split for Desktop & Tablet */}
          {currentStep === 1 && (
            <motion.div
              key="step-subscription"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-8xl mx-auto space-y-6"
            >
              {/* Desktop/Tablet Header Text */}
              <div className="text-center sm:text-left space-y-1">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
                  Choose Your Membership Plan
                </h2>
                <p className="text-sm sm:text-base text-gray-500 font-medium">
                  Zero commission on fan requests and audience tips. Select your pack.
                </p>
              </div>

              {/* Responsive Grid: Left Column (Banner, Perks, What's Included) & Right Column (Plans, Toggle, CTA) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                {/* Left Column (Desktop 6 cols) */}
                <div className="lg:col-span-7 space-y-5">
                  {/* Top Banner Card Carousel with real CDN images */}
                  <div className="w-full rounded-3xl bg-gradient-to-r from-purple-50 via-white to-purple-50 border border-purple-100 p-4 relative overflow-hidden shadow-sm">
                    <div className="relative w-full h-44 sm:h-56 rounded-2xl overflow-hidden bg-gray-100">
                      {bannerList && bannerList.length > 0 && bannerList[activeBannerIndex] ? (
                        <img
                          src={bannerList[activeBannerIndex]}
                          alt="Subscription Banner"
                          className="w-full h-full object-cover transition-all duration-700"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#6720A4] to-[#853068] text-white p-6 flex items-center justify-between">
                          <div className="max-w-[70%] space-y-2">
                            <h3 className="text-xl sm:text-2xl font-black leading-tight">
                              Let your crowd request the music
                            </h3>
                            <p className="text-xs sm:text-sm text-white/80">
                              Take song requests without leaving the DJ booth.
                            </p>
                          </div>
                          <Music className="w-12 h-12 text-white/80" />
                        </div>
                      )}

                      {/* Carousel Arrow Navigation */}
                      <button
                        type="button"
                        onClick={() =>
                          setActiveBannerIndex(
                            (prev) => (prev - 1 + bannerList.length) % bannerList.length
                          )
                        }
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-xs transition-colors"
                        aria-label="Previous banner"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setActiveBannerIndex((prev) => (prev + 1) % bannerList.length)
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-xs transition-colors"
                        aria-label="Next banner"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* 3 dots carousel indicator */}
                    <div className="flex items-center justify-center gap-2 mt-3">
                      {bannerList.map((_, dotIdx) => (
                        <span
                          key={dotIdx}
                          onClick={() => setActiveBannerIndex(dotIdx)}
                          className={`h-2 rounded-full transition-all cursor-pointer ${
                            activeBannerIndex === dotIdx
                              ? "w-6 bg-[#6720A4]"
                              : "w-2 bg-gray-300 hover:bg-gray-400"
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Commission & Perks Row */}
                  <div className="grid grid-cols-3 gap-1 bg-white px-2 py-3 rounded-2xl border border-gray-200/80 shadow-xs">
                    <div className="flex items-center gap-2 p-1">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-[#6720A4] text-xs font-black flex items-center justify-center shrink-0">
                        0%
                      </div>
                      <span className="text-xs font-bold text-gray-700 leading-tight">
                        0% commission on requests
                      </span>
                    </div>

                    <div className="flex items-center gap-2 p-1 border-x border-gray-150">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-[#6720A4] text-xs font-black flex items-center justify-center shrink-0">
                        0%
                      </div>
                      <span className="text-xs font-bold text-gray-700 leading-tight">
                        0% commission on tips
                      </span>
                    </div>

                    <div className="flex items-center gap-2 p-1">
                      <div className="w-7 h-7 rounded-full bg-purple-100 text-[#6720A4] text-xs font-bold flex items-center justify-center shrink-0">
                        <TrendingUp className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-gray-700 leading-tight">
                        Upgrade anytime
                      </span>
                    </div>
                  </div>

                  {/* What's Included Card List */}
                  <div className="bg-white p-5 rounded-3xl border border-gray-200/80 shadow-xs space-y-3">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                      What&apos;s Included In All Packs
                    </h4>
                    <div className="space-y-2.5">
                      {featuresList.map((feat, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5 stroke-[3]" />
                          </div>
                          <span className="text-xs sm:text-sm font-medium text-gray-700">
                            {feat}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right Column (Desktop 6 cols) - Pricing & Payment */}
                <div className="lg:col-span-5 space-y-4">
                  {/* Quarterly vs Annual Switcher */}
                  <div className="flex justify-center sm:justify-start">
                    <div className="bg-gray-200/80 p-1.5 rounded-full flex items-center w-full max-w-sm shadow-inner">
                      <button
                        type="button"
                        onClick={() => setBillingInterval("quarterly")}
                        className={`flex-1 py-2 rounded-full text-xs font-bold transition-all text-center ${
                          billingInterval === "quarterly"
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Quarterly
                      </button>

                      <button
                        type="button"
                        onClick={() => setBillingInterval("annual")}
                        className={`flex-1 py-2 rounded-full text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                          billingInterval === "annual"
                            ? "bg-white text-[#6720A4] shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <span>Annual</span>
                        <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                          SAVE 49%
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Backend Error Alert Box (if error occurs, stop here and show backend error) */}
                  {subscriptionError && (
                    <div className="p-4 rounded-2xl bg-red-50 border border-red-200 flex items-start gap-3 text-red-700 animate-fadeIn">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-600" />
                      <div className="flex-1 text-xs sm:text-sm">
                        <span className="font-bold block">Payment / Subscription Error:</span>
                        <span>{subscriptionError}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSubscriptionError(null)}
                        className="text-red-500 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Plan Selection Cards */}
                  {loadingPlans ? (
                    <div className="py-14 flex flex-col items-center justify-center space-y-2">
                      <Loader2 className="w-8 h-8 animate-spin text-[#6720A4]" />
                      <p className="text-xs text-gray-400 font-medium">Loading subscription plans...</p>
                    </div>
                  ) : currentPlans.length === 0 ? (
                    <div className="py-12 flex flex-col items-center justify-center gap-3 text-center px-4 bg-white rounded-2xl border border-gray-200">
                      <AlertCircle className="w-8 h-8 text-amber-500" />
                      <p className="text-xs sm:text-sm font-medium text-gray-700">
                        {subscriptionError || "No subscription plans available from server."}
                      </p>
                      <button
                        type="button"
                        onClick={fetchPlans}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#6720A4] text-white text-xs font-bold hover:bg-[#551887] transition-all"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        <span>Retry</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {currentPlans.map((plan, idx) => {
                        const isSelected = selectedPlanIndex === idx;
                        const intervalData =
                          billingInterval === "annual" ? plan.annual : plan.quarterly;
                        const rawAmountPaise = intervalData?.amount ?? 0;
                        const totalRupees = Math.round(rawAmountPaise / 100);
                        const monthsCount = billingInterval === "annual" ? 12 : 3;
                        const monthlyEquivalent = Math.round(totalRupees / monthsCount);

                        const maxLimitText =
                          intervalData?.maxEarnings != null
                            ? `Earn up to Rs. ${intervalData.maxEarnings.toLocaleString("en-IN")} ${
                                billingInterval === "annual" ? "annually" : "quarterly"
                              }`
                            : intervalData?.maxGigValue != null
                            ? `Earn up to Rs. ${(intervalData.maxGigValue).toLocaleString(
                                "en-IN"
                              )} ${billingInterval === "annual" ? "annually" : "quarterly"}`
                            : "Unlimited earning potential";

                        return (
                          <div
                            key={plan.type || idx}
                            onClick={() => {
                              setSelectedPlanIndex(idx);
                              setSubscriptionError(null);
                            }}
                            className={`p-4 sm:p-5 rounded-2xl border-2 transition-all cursor-pointer bg-white flex items-center justify-between hover:shadow-sm ${
                              isSelected
                                ? "border-[#6720A4] shadow-md ring-2 ring-[#6720A4]/20 bg-purple-50/10"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              {/* Custom Radio Button */}
                              <div
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                  isSelected
                                    ? "border-[#6720A4]"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {isSelected && (
                                  <div className="w-3 h-3 rounded-full bg-[#6720A4]" />
                                )}
                              </div>

                              <div>
                                <h4 className="text-base font-extrabold text-gray-900 tracking-wide uppercase">
                                  {plan.type || `Plan ${idx + 1}`}
                                </h4>
                                <p className="text-xs text-gray-500 font-medium mt-0.5">
                                  {maxLimitText}
                                </p>
                                <span className="text-[11px] text-gray-400 block mt-0.5">
                                  Total: ₹{totalRupees.toLocaleString("en-IN")} / {billingInterval === "annual" ? "year" : "quarter"}
                                </span>
                              </div>
                            </div>

                            <div className="text-right">
                              <div className="text-lg sm:text-xl font-black text-gray-900">
                                ₹{monthlyEquivalent}
                                <span className="text-xs font-medium text-gray-500">
                                  /mo
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Price Helper Subtext */}
                  <p className="text-center sm:text-left text-xs text-gray-400 font-medium">
                    {billingInterval === "annual"
                      ? "Prices shown monthly. Billed annually."
                      : "Prices shown monthly. Billed quarterly."}
                  </p>

                  {/* Big Purple CTA Button */}
                  <div className="pt-2 space-y-2.5">
                    <button
                      type="button"
                      disabled={payingSubscription || currentPlans.length === 0}
                      onClick={handleSubscriptionPayment}
                      className="w-full bg-[#6720A4] hover:bg-[#551887] active:scale-[0.99] text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-all disabled:opacity-60 text-base"
                    >
                      {payingSubscription ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Initiating Paytm Payment...</span>
                        </>
                      ) : (
                        <>
                          <span>Continue with {selectedTierName}</span>
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    {/* Security & Refund Assurance */}
                    <div className="flex items-center justify-center gap-3 text-xs font-semibold text-emerald-600">
                      <span className="flex items-center gap-1">
                        <ShieldCheck className="w-4 h-4" />
                        Secure payment
                      </span>
                      <span>•</span>
                      <span>Cancel renewal anytime</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 2: "Tell Us About You" - Wide Responsive Layout */}
          {currentStep === 2 && (
            <motion.div
              key="step-profile"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-3xl mx-auto space-y-6"
            >
              <div className="text-center space-y-1">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Tell Us About You
                </h2>
                <p className="text-sm text-gray-500 font-medium">
                  Set up your public performer identity for venues and fans
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-150 space-y-5">
                {/* Photo Upload */}
                <div className="flex flex-col items-center justify-center pb-2">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full overflow-hidden bg-purple-50 border-4 border-purple-100 shadow-md flex items-center justify-center">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-10 h-10 text-[#6720A4]" />
                      )}
                    </div>
                    <label
                      htmlFor="avatar-input"
                      className="absolute bottom-0 right-0 p-2 rounded-full bg-[#6720A4] text-white shadow-md cursor-pointer hover:bg-[#551887] transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                    </label>
                    <input
                      id="avatar-input"
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </div>
                  <span className="text-xs text-gray-400 mt-2 font-medium">
                    {uploadingAvatar ? "Uploading photo..." : "Upload Profile Photo"}
                  </span>
                </div>

                {/* Stage Name & Legal Name Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Stage / Performer Name *
                    </label>
                    <input
                      type="text"
                      value={stageName}
                      onChange={(e) => setStageName(e.target.value)}
                      placeholder="e.g. DJ Shadow, Riya Live"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Full Legal Name *
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="e.g. Aditya Sharma"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4]"
                    />
                  </div>
                </div>

                {/* Email & Gender Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e.g. artist@example.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Gender *
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {["Male", "Female", "Other"].map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGender(g)}
                          className={`py-2.5 rounded-xl text-xs font-bold transition-all border ${
                            gender === g
                              ? "bg-[#6720A4] text-white border-[#6720A4]"
                              : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* DOB */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Date of Birth *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <select
                      value={dob.day}
                      onChange={(e) => setDob({ ...dob, day: e.target.value })}
                      className="px-3 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#6720A4] bg-white"
                    >
                      <option value="">Day</option>
                      {days.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>

                    <select
                      value={dob.month}
                      onChange={(e) => setDob({ ...dob, month: e.target.value })}
                      className="px-3 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#6720A4] bg-white"
                    >
                      <option value="">Month</option>
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>

                    <select
                      value={dob.year}
                      onChange={(e) => setDob({ ...dob, year: e.target.value })}
                      className="px-3 py-2.5 rounded-xl border border-gray-200 text-xs focus:outline-none focus:border-[#6720A4] bg-white"
                    >
                      <option value="">Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Short Bio
                  </label>
                  <textarea
                    rows={3}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell fans about your performance background, resident clubs, and musical style..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] resize-none"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (validateStep2()) setCurrentStep(3);
                }}
                className="w-full bg-[#6720A4] hover:bg-[#551887] text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-all text-base"
              >
                <span>Continue to Location</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {/* STEP 3: "Where You Perform" - Wide Responsive Layout */}
          {currentStep === 3 && (
            <motion.div
              key="step-location"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-3xl mx-auto space-y-6"
            >
              <div className="text-center space-y-1">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                  Where You Perform
                </h2>
                <p className="text-sm text-gray-500 font-medium">
                  Select your primary city and travel availability for event bookings
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-gray-150 space-y-5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Primary City *
                  </label>
                  <PlacesAutocomplete
                    value={city}
                    onChange={setCity}
                    onSelect={handleSelectPrimaryCity}
                    searchOptions={{
                      componentRestrictions: { country: "in" },
                    }}
                  >
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                      <div className="relative">
                        <MapPin className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                        <input
                          {...getInputProps({
                            placeholder: "Search or enter primary city (e.g. Mumbai, Bengaluru)",
                            className:
                              "w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors",
                          })}
                        />
                        {suggestions.length > 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto divide-y divide-gray-50">
                            {loading && (
                              <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Searching locations...</span>
                              </div>
                            )}
                            {suggestions.map((suggestion) => {
                              const active = suggestion.active;
                              return (
                                <div
                                  key={suggestion.placeId}
                                  {...getSuggestionItemProps(suggestion, {
                                    className: `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-3 transition-colors ${
                                      active
                                        ? "bg-purple-50 text-[#6720A4] font-medium"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`,
                                  })}
                                >
                                  <div
                                    className={`p-1.5 rounded-lg shrink-0 ${
                                      active ? "bg-purple-100 text-[#6720A4]" : "bg-gray-100 text-gray-400"
                                    }`}
                                  >
                                    <MapPin className="w-3.5 h-3.5" />
                                  </div>
                                  <span className="truncate">{suggestion.description}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                      Locality / Area
                    </label>
                    <PlacesAutocomplete
                      value={area}
                      onChange={setArea}
                      onSelect={handleSelectLocality}
                      searchOptions={{
                        componentRestrictions: { country: "in" },
                      }}
                    >
                      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                        <div className="relative">
                          <input
                            {...getInputProps({
                              placeholder: "e.g. Powai, Bandra, Indiranagar",
                              className:
                                "w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors",
                            })}
                          />
                          {suggestions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto divide-y divide-gray-50">
                              {loading && (
                                <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  <span>Searching localities...</span>
                                </div>
                              )}
                              {suggestions.map((suggestion) => {
                                const active = suggestion.active;
                                return (
                                  <div
                                    key={suggestion.placeId}
                                    {...getSuggestionItemProps(suggestion, {
                                      className: `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-3 transition-colors ${
                                        active
                                          ? "bg-purple-50 text-[#6720A4] font-medium"
                                          : "bg-white text-gray-700 hover:bg-gray-50"
                                      }`,
                                    })}
                                  >
                                    <div
                                      className={`p-1.5 rounded-lg shrink-0 ${
                                        active ? "bg-purple-100 text-[#6720A4]" : "bg-gray-100 text-gray-400"
                                      }`}
                                    >
                                      <MapPin className="w-3.5 h-3.5" />
                                    </div>
                                    <span className="truncate">{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </PlacesAutocomplete>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="e.g. Maharashtra, Karnataka"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                    Travel Availability
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {TRAVEL_OPTIONS.map(({ id, label, icon: Icon }) => {
                      const isSelected = selectedTravel.includes(id);
                      return (
                        <div
                          key={id}
                          onClick={() => toggleTravelOption(id)}
                          className={`cursor-pointer p-3.5 rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center gap-2 ${
                            isSelected
                              ? "border-[#6720A4] bg-purple-50 text-[#6720A4] font-bold shadow-xs"
                              : "border-gray-200 bg-white text-gray-700 font-medium hover:border-gray-300"
                          }`}
                        >
                          <Icon className="w-5 h-5 shrink-0" />
                          <span className="text-xs">{label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1">
                    Other Cities You Frequently Perform In
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <PlacesAutocomplete
                        value={newCityInput}
                        onChange={setNewCityInput}
                        onSelect={handleSelectOtherCity}
                        searchOptions={{
                          types: ["(cities)"],
                          componentRestrictions: { country: "in" },
                        }}
                      >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                          <div className="relative">
                            <input
                              {...getInputProps({
                                placeholder: "Add city (e.g. Goa, Pune)",
                                onKeyDown: (e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddCity();
                                  }
                                },
                                className:
                                  "w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#6720A4] transition-colors",
                              })}
                            />
                            {suggestions.length > 0 && (
                              <div className="absolute left-0 right-0 top-full mt-1.5 z-50 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto divide-y divide-gray-50">
                                {loading && (
                                  <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    <span>Searching cities...</span>
                                  </div>
                                )}
                                {suggestions.map((suggestion) => {
                                  const active = suggestion.active;
                                  return (
                                    <div
                                      key={suggestion.placeId}
                                      {...getSuggestionItemProps(suggestion, {
                                        className: `px-4 py-2.5 text-sm cursor-pointer flex items-center gap-3 transition-colors ${
                                          active
                                            ? "bg-purple-50 text-[#6720A4] font-medium"
                                            : "bg-white text-gray-700 hover:bg-gray-50"
                                        }`,
                                      })}
                                    >
                                      <div
                                        className={`p-1.5 rounded-lg shrink-0 ${
                                          active ? "bg-purple-100 text-[#6720A4]" : "bg-gray-100 text-gray-400"
                                        }`}
                                      >
                                        <MapPin className="w-3.5 h-3.5" />
                                      </div>
                                      <span className="truncate">{suggestion.description}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddCity}
                      className="px-5 py-2.5 rounded-xl bg-purple-100 text-[#6720A4] font-bold text-xs hover:bg-purple-200 transition-colors shrink-0"
                    >
                      Add City
                    </button>
                  </div>

                  {additionalCities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {additionalCities.map((c) => (
                        <span
                          key={c}
                          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                        >
                          <span>{c}</span>
                          <X
                            className="w-3.5 h-3.5 cursor-pointer hover:text-red-500"
                            onClick={() => handleRemoveCity(c)}
                          />
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleCompleteRegistration}
                disabled={isSubmitting}
                className="w-full bg-[#6720A4] hover:bg-[#551887] text-white font-bold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 transition-all disabled:opacity-60 text-base"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating Your Partner Account...</span>
                  </>
                ) : (
                  <>
                    <span>Complete &amp; Go Live</span>
                    <Sparkles className="w-5 h-5" />
                  </>
                )}
              </button>
            </motion.div>
          )}


        </AnimatePresence>
      </main>

      {/* "Your Specialities" Modal / Bottom Sheet (Responsive for Mobile, Tablet, and Desktop) */}
      <AnimatePresence>
        {showSpecialtiesModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 animate-fadeIn">
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="w-full max-w-xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 space-y-4 max-h-[85vh] flex flex-col border border-gray-150"
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight font-sans">
                    Your Specialities
                  </h3>
                  <p className="text-xs text-gray-500">
                    Select up to 3 genres you specialize in for {selectedRole?.displayName || "this category"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSpecialtiesModal(false)}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={specialtiesSearch}
                  onChange={(e) => setSpecialtiesSearch(e.target.value)}
                  placeholder="Search specialities (e.g. House, Techno, Bollywood)..."
                  className="w-full pl-10 pr-4 py-3 rounded-2xl bg-gray-100/80 border border-transparent text-sm focus:outline-none focus:bg-white focus:border-[#6720A4] transition-all"
                />
              </div>

              {/* Checkbox List - 2 columns on tablet/desktop */}
              <div className="flex-1 overflow-y-auto pr-1 max-h-72">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {currentRoleGenres.map((genre) => {
                    const isChecked = selectedSpecialties.includes(genre);
                    const isMaxReached = !isChecked && selectedSpecialties.length >= 3;
                    return (
                      <div
                        key={genre}
                        onClick={() => toggleSpecialty(genre)}
                        className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                          isChecked
                            ? "border-[#6720A4] bg-purple-50/50"
                            : isMaxReached
                            ? "border-gray-150 bg-gray-50/50 opacity-60 hover:border-gray-200"
                            : "border-gray-150 hover:border-gray-200 bg-white"
                        }`}
                      >
                        <span className={`text-sm font-medium ${isChecked ? "text-[#6720A4] font-semibold" : isMaxReached ? "text-gray-400" : "text-gray-800"}`}>
                          {genre}
                        </span>
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                            isChecked
                              ? "bg-[#6720A4] border-[#6720A4] text-white"
                              : isMaxReached
                              ? "border-gray-300 bg-gray-100 text-transparent"
                              : "border-gray-400 bg-white"
                          }`}
                        >
                          {isChecked && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Action Bar */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                <span className="text-xs font-bold text-gray-600">
                  {selectedSpecialties.length} / 3 Selected
                </span>
                <button
                  type="button"
                  onClick={handleSpecialtiesContinue}
                  className="px-6 py-3 rounded-2xl bg-[#6720A4] hover:bg-[#551887] active:scale-[0.98] text-white font-bold text-sm shadow-md shadow-purple-200 flex items-center gap-2 transition-all"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
